import axios from 'axios';
import {useEffect, useRef} from 'react';
import URL_CONST from '../../../config/url';
import {DataPrepareUploadProgress} from '../../../store/dataPrepare';
import {Status} from '../../../types/operation/Status';
import fileSize from 'filesize';
import {BinariesUploadResponse} from '../../../types/dataService/Binaries';
import {toast} from '../../../base-components/StudioToast';
import {getHeaders, TEMP_DATASERVICE_HEADERS} from '../../../store/dataService';
import {ItemType} from '../../../base-components/StudioDataset/FileViewer';
import {useIntl} from 'react-intl';

export type AddRawDataProps = {
  datasetId?: string | null;
  version?: number;
  previewSize?: number;
  onResume?: () => void;
  onStart?: () => void;
  onUploadProgress?: (event: DataPrepareUploadProgress) => void;
  onStatus?: (status: Status) => void;
  onFinish?: (res: BinariesUploadResponse, filesUploaded: ItemType[]) => void;
  onError?: (error: unknown) => void;
};

export const useAddRawData = ({
  datasetId,
  version,
  previewSize = 90,
  onResume,
  onStart,
  onUploadProgress,
  onStatus,
  onFinish,
  onError,
}: AddRawDataProps) => {
  const intl = useIntl();
  const onResumeRef = useRef(onResume);
  const onStartRef = useRef(onStart);
  const onUploadProgressRef = useRef(onUploadProgress);
  const onStatusRef = useRef(onStatus);
  const onFinishRef = useRef(onFinish);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onResumeRef.current = onResume;
    onStartRef.current = onStart;
    onUploadProgressRef.current = onUploadProgress;
    onStatusRef.current = onStatus;
    onFinishRef.current = onFinish;
    onErrorRef.current = onError;
  }, [onResume, onStart, onUploadProgress, onStatus, onFinish, onError]);

  const uploadFileItems = async (fileItems: Array<ItemType>) => {
    onStartRef.current?.();
    const formData = new FormData();
    for (const fileItem of fileItems) {
      if (fileItem.file) {
        formData.append('file', fileItem.file, fileItem.filename);
      }
    }

    const {data} = await axios.post<BinariesUploadResponse>(
      URL_CONST.DSS_DATASET_BINARIES,
      formData,
      {
        headers: await getHeaders(TEMP_DATASERVICE_HEADERS),
        onUploadProgress: progressEvent =>
          onUploadProgressRef.current?.({
            progress: Math.round((progressEvent.loaded * 100) / progressEvent.total),
            total: fileSize(progressEvent.total),
          }),
        params: {
          datasetId,
          previewDimensions: `${previewSize}x${previewSize}`,
        },
      }
    );
    // TODO: Add error response
    // if (data.errors?.length) {
    // if (data.statusLog.length) {
    //   throw new Error(data.statusLog[0].message);
    // }
    return data;
  };

  const initiate = async (fileItems: Array<ItemType>) => {
    if (fileItems.length) {
      try {
        const response = await uploadFileItems(fileItems);
        toast.success(
          intl.formatMessage(
            {
              id: `dataPrep.uploading.${
                fileItems.length > 1 ? 'multipleFile' : 'oneFile'
              }.success`,
            },
            {count: fileItems.length}
          )
        );
        onFinishRef && onFinishRef.current && onFinishRef.current(response, fileItems);
      } catch (e) {
        onErrorRef.current?.(e);
      }
    }
  };
  return initiate;
};
